import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Me } from "../types/models/me";
import { EntrepriseCustomDataSetting } from "../types/models/entreprise-custom-data-setting";
import { useKmsService } from "./kms-service-provider";
import { useAuth } from "./keycloakServiceProvider";
import { showToast } from "../components/shared/feedback/custom-toast-shortcut";
import { EntrepriseType } from "../types/records/entreprise-type-record";

type EntrepriseAdministree = {
  id: number;
  nom: string;
  type: number;
};

type EntrepriseActive = {
  idEntreprise: number;
  nom: string;
  isClient: boolean;
  isPartenaire: boolean;
  factureAutorisee: boolean;
  /** Tous les champs personnalisés de l'entreprise, toutes cibles confondues. */
  customDataSettings?: EntrepriseCustomDataSetting[];
};

export type User = {
  idKeycloak: string;
  idContact: number;
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  entrepriseActive?: EntrepriseActive;
  // Entreprises administrées (gestionnaire) : sert aux contrôles d'autorisation
  // (cf. isEntrepriseManager, isManagerOfDriver/Client) - ne pas confondre avec
  // entreprisesDisponibles qui liste toutes les entreprises dont le contact fait
  // partie, y compris en tant que simple passager/chauffeur.
  entreprises?: EntrepriseAdministree[];
  entreprisesDisponibles?: EntrepriseAdministree[];
  chauffeur?: {
    id: number;
    nomEntreprise: string | null;
  };
  passager?: {
    id: number;
    entreprise: {
      id: number;
      nom: string;
      factureAutorisee: boolean;
    } | null;
    minimalService: {
      id: number;
      nom: string;
      priorite: number;
    } | null;
    isGestionnaire: boolean;
    isDisabled: boolean;
    customData: Record<string, string>;
  };
  role: string[];
  picture: string;
};

type UserContextType = {
  me: User | null;
  isLoading: boolean;
  isSwitchingEntreprise: boolean;
  /**
   * Le compte a des entreprises mais a choisi de n'en activer aucune : ses
   * courses sont réservées à titre privé. À distinguer de l'utilisateur qui
   * n'a tout simplement aucune entreprise — les écrans de gestion d'entreprise
   * proposent d'en créer une dans ce second cas seulement.
   */
  isModePrive: boolean;
  logout: () => void;
  reload: () => void;
  /** `null` = à titre privé, cf. {@link UserContextType.isModePrive}. */
  selectEntreprise: (id: number | null) => Promise<void>;
};

/** Libellé du mode privé, partagé par le sélecteur d'entreprise et les écrans. */
export const MODE_PRIVE_LABEL = "À titre privé";

const UserContext = createContext<UserContextType | null>(null);

export const UserProvider = ({ children }: { children?: ReactNode }) => {
  const { authenticated, user, logout } = useAuth();
  const { userService } = useKmsService();

  const [isLoading, setIsLoading] = useState(true);
  const [me, setMe] = useState<User | null>(null);
  const [isSwitchingEntreprise, setIsSwitchingEntreprise] = useState(false);

  const init = useCallback(async () => {
    try {
      if (authenticated) {
        const data: Me = await userService.me();

        setMe({
          idKeycloak: user.sub,
          idContact: data.id,
          nom: data.nom,
          prenom: data.prenom,
          email: data.email,
          telephone: data.telephone,
          entrepriseActive: data.entrepriseActive,
          entreprises: data.entrepriseAdministrateurs ?? [],
          entreprisesDisponibles: data.entreprises ?? [],
          chauffeur: data.chauffeur,
          passager: data.passager,
          picture: user.picture,
          role: user?.realm_access?.roles ?? [],
        });
      } else {
        setMe(null);
      }
    } catch (error) {
      console.error("Failed to fetch user:", error);
      setMe(null);
    } finally {
      setIsLoading(false);
    }
  }, [authenticated, user]);

  useEffect(() => {
    init();
  }, [init]);

  const selectEntreprise = useCallback(
    async (id: number | null) => {
      // Normalisation indispensable : `entrepriseActive` est absent (undefined)
      // en mode privé, et `undefined === null` est faux — sans cela, choisir
      // « à titre privé » deux fois de suite relancerait un appel inutile.
      if ((me?.entrepriseActive?.idEntreprise ?? null) === id) return;

      setIsSwitchingEntreprise(true);
      try {
        // Le backend recalcule les infos client (nomEntreprise, idEntreprise, ...)
        // pour qu'elles reflètent toujours l'entreprise active.
        await userService.switchActiveEntreprise(id);
        await init();
      } catch (error) {
        console.error("Erreur lors du changement d'entreprise active:", error);
        showToast.errorApi(error);
      } finally {
        setIsSwitchingEntreprise(false);
      }
    },
    [me, userService],
  );

  const isModePrive =
    !!me && !me.entrepriseActive && (me.entreprisesDisponibles?.length ?? 0) > 0;

  return (
    <UserContext.Provider
      value={{
        me,
        isLoading,
        isSwitchingEntreprise,
        isModePrive,
        logout,
        reload: init,
        selectEntreprise,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const ctx = useContext(UserContext);

  if (!ctx) {
    throw new Error("useIoService must be used within ServiceElectronProvider");
  }

  return ctx;
};
